// src/pages/customer/CustomerHome.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { loyalty as loyaltyApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { MyCodeSheet } from './CustomerDebts';

export default function CustomerHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const navItems = useCustomerNav();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  // «Мой QR» — на главной, чтобы у кассы открыть в одно касание
  const [codeOpen, setCodeOpen] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setCards(await loyaltyApi.cards()); } catch (e) { setCards([]); }
    setLoading(false);
  };

  const totalPoints = cards.reduce((s, c) => s + (c.points || 0), 0);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div style={{ padding: '20px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="page-subtitle">{t('customerHome.hello')}</p>
            <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || user?.email?.split('@')[0] || t('customerHome.customerFallback')}
            </h1>
          </div>
          <button onClick={() => setCodeOpen(true)}
            style={{
              background: 'var(--brand-primary)', border: 'none',
              borderRadius: 'var(--r-sm)', padding: '8px 12px',
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--font-display)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              flexShrink: 0,
            }}
          >
            <Icon name="qr" size={18} stroke={2} />
            {t('myCode.button')}
          </button>
          <button onClick={() => { logout(); navigate('/'); }}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', padding: '8px 12px',
              color: 'var(--fg-muted)', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--font-display)',
              flexShrink: 0,
            }}
          >
            {t('customerHome.switchAcc')}
          </button>
        </div>

        {/* Hero баланса баллов */}
        <div className="hero-loyalty" style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.08em' }}>{t('customerHome.totalPointsLabel')}</p>
          <p className="hero-num" style={{ fontSize: 44, marginTop: 4 }}>
            {totalPoints.toLocaleString()}
          </p>
          <p style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>
            <Icon name="starFilled" size={13} stroke={0} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
            {t(cards.length === 1 ? 'customerHome.atShops_one' : 'customerHome.atShops_other', { count: cards.length })}
          </p>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Главное действие — сканер */}
        <button
          onClick={() => navigate('/scan')}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: 14,
            padding: 18,
            background: 'var(--brand-accent)',
            border: 'none', borderRadius: 'var(--r-md)',
            color: '#fff', cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(6, 182, 212, 0.25)',
            marginBottom: 10,
            fontFamily: 'var(--font-display)',
            transition: 'transform 120ms var(--ease-spring)',
          }}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={(e) => e.currentTarget.style.transform = ''}
          onMouseLeave={(e) => e.currentTarget.style.transform = ''}
        >
          <div style={{
            width: 48, height: 48, borderRadius: 'var(--r-sm)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name="scan" size={24} stroke={2} />
          </div>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <p style={{ fontSize: 16, fontWeight: 800 }}>{t('customerHome.scanReceipt')}</p>
            <p style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {t('customerHome.scanReceiptDesc')}
            </p>
          </div>
          <Icon name="chevronRight" size={18} stroke={2.5} />
        </button>

        {/* Оплатить баллами */}
        <button
          onClick={() => navigate('/pay')}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: 14,
            padding: 18,
            background: 'var(--brand-primary)',
            border: 'none', borderRadius: 'var(--r-md)',
            color: '#fff', cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(99, 102, 241, 0.25)',
            marginBottom: 10,
            fontFamily: 'var(--font-display)',
            transition: 'transform 120ms var(--ease-spring)',
          }}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={(e) => e.currentTarget.style.transform = ''}
          onMouseLeave={(e) => e.currentTarget.style.transform = ''}
        >
          <div style={{
            width: 48, height: 48, borderRadius: 'var(--r-sm)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name="wallet" size={24} stroke={2} />
          </div>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <p style={{ fontSize: 16, fontWeight: 800 }}>{t('customerHome.payWithPoints')}</p>
            <p style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {t('customerHome.payWithPointsDesc')}
            </p>
          </div>
          <Icon name="chevronRight" size={18} stroke={2.5} />
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => navigate('/catalog')}
          style={{ marginBottom: 24 }}
        >
          <Icon name="store" size={18} />
          {t("customerHome.findShop")}
        </button>

        {cards.length === 0 ? (
          <div className="empty-state">
            <div className="icon"><Icon name="creditCard" size={28} stroke={1.5} /></div>
            <p>
              {t("customerHome.noCardsHint")}
            </p>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 10, letterSpacing: '0.08em' }}>
              {t("customerHome.myShops")}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {cards.map(card => (
                <div key={card.id} className="card" style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/shop/${card.shopId}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {card.shop?.name || card.shopId}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                        {t('customerHome.purchasesAndSpent', { count: card.totalPurchases || 0, amount: formatMoney(card.totalSpent || 0, card.shop?.currency) })}
                      </p>
                    </div>
                    <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                      {(card.points || 0).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ background: 'var(--bg-subtle)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, ((card.points % (card.shop?.minPointsToRedeem || 500)) / (card.shop?.minPointsToRedeem || 500)) * 100)}%`,
                      background: 'linear-gradient(90deg, var(--brand-primary), var(--brand-accent))',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {codeOpen && <MyCodeSheet onClose={() => setCodeOpen(false)} />}

      <BottomNav items={navItems} />
    </div>
  );
}

const CUSTOMER_NAV_BASE = [

  { path: '/',        labelKey: 'nav.home',     match: p => p === '/',                                          iconName: 'home' },
  { path: '/cards',   labelKey: 'nav.cards',    match: p => p.startsWith('/cards'),                             iconName: 'creditCard' },
  { path: '/catalog', labelKey: 'nav.catalog',  match: p => p.startsWith('/catalog') || p.startsWith('/shop'), iconName: 'store' },
  { path: '/profile', labelKey: 'nav.profile',  match: p => p.startsWith('/profile'),                          iconName: 'user' },
];

export function useCustomerNav() {
  // eslint-disable-next-line
  const { t, i18n } = useTranslation();
  return CUSTOMER_NAV_BASE.map(item => ({ ...item, label: t(item.labelKey) }));
}

// Совместимость
export const CUSTOMER_NAV = CUSTOMER_NAV_BASE.map(item => ({
  ...item,
  label: { 'nav.home': 'Главная', 'nav.cards': 'Карты', 'nav.catalog': 'Витрины', 'nav.profile': 'Профиль' }[item.labelKey],
}));
